
import { useState, useEffect } from "react";
import { Requirement, RequirementStatus } from "@/types/project";
import { DocumentsDialog } from "./DocumentsDialog";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { RequirementRow } from "./RequirementRow";

interface RequirementsTableProps {
  requirements: Requirement[];
  onViewGuidance: (requirement: Requirement) => void;
  onUploadDocumentation: (requirement: Requirement) => void;
  onMarkNotApplicable: (requirement: Requirement) => void;
  onMarkReadyForReview?: (requirement: Requirement) => void;
  onMarkIncomplete?: (requirement: Requirement) => void;
  requirementStatuses?: RequirementStatus[];
  isStatusLoading?: boolean;
  getRequirementStatus?: (requirement: Requirement) => string;
  projectId?: string;
}

interface DocumentCount {
  requirementId: string;
  count: number;
  projectId: string;
  statuses: Record<string, number>;
}

export function RequirementsTable({ 
  requirements, 
  onViewGuidance, 
  onUploadDocumentation, 
  onMarkNotApplicable,
  onMarkReadyForReview,
  onMarkIncomplete,
  requirementStatuses = [],
  isStatusLoading = false,
  getRequirementStatus,
  projectId 
}: RequirementsTableProps) {
  const [selectedRequirement, setSelectedRequirement] = useState<Requirement | null>(null);
  const [isDocumentsDialogOpen, setIsDocumentsDialogOpen] = useState(false);
  const [documentCounts, setDocumentCounts] = useState<DocumentCount[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (projectId && requirements.length > 0) {
      fetchDocumentCounts();
    }
  }, [projectId, requirements]);

  const fetchDocumentCounts = async () => {
    if (!projectId) {
      console.log("Missing projectId, cannot fetch document counts");
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Get current user ID for filtering
      const { data: userData } = await supabase.auth.getUser();
      const currentUserId = userData.user?.id;
      
      if (!currentUserId) {
        console.log("No user ID found, cannot fetch document counts");
        return;
      }
      
      console.log(`Fetching document counts for project ${projectId} and user ${currentUserId}`);
      
      const { data, error } = await supabase
        .from('requirement_documents')
        .select('requirement_id, status')
        .eq('project_id', projectId)
        .eq('user_id', currentUserId);
      
      if (error) throw error;
      
      const counts: Record<string, DocumentCount> = {};
      
      data.forEach(doc => {
        const reqId = doc.requirement_id;
        
        if (!counts[reqId]) {
          counts[reqId] = {
            requirementId: reqId,
            count: 0,
            projectId: projectId,
            statuses: {}
          };
        }
        
        counts[reqId].count += 1;
        
        if (!counts[reqId].statuses[doc.status]) {
          counts[reqId].statuses[doc.status] = 0;
        }
        
        counts[reqId].statuses[doc.status] += 1;
      });
      
      setDocumentCounts(Object.values(counts));
      console.log("Document counts:", Object.values(counts));
    } catch (error) {
      console.error("Error fetching document counts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadDocumentation = (requirement: Requirement) => {
    if (projectId) {
      setSelectedRequirement(requirement);
      setIsDocumentsDialogOpen(true);
    } else {
      onUploadDocumentation(requirement);
    }
  };

  const handleStatusChange = (requirement: Requirement, status: string) => {
    // Determine which handler to call based on status
    switch (status) {
      case 'Not Applicable':
        if (onMarkNotApplicable) onMarkNotApplicable(requirement);
        break;
      case 'Ready to Submit':
        if (onMarkReadyForReview) onMarkReadyForReview(requirement);
        break;
      case 'Incomplete':
        if (onMarkIncomplete) onMarkIncomplete(requirement);
        break;
      case 'In Progress':
        if (onUploadDocumentation) onUploadDocumentation(requirement);
        break;
      default:
        toast.info(`Changed status to ${status}`);
    }
  };

  return (
    <div className="bg-card border rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Requirements</h2>
        <p className="text-sm text-muted-foreground">
          {requirements.length || 0} requirements
        </p>
      </div>
      
      {requirements && requirements.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="py-2 px-4 text-left font-medium">Section</th>
                <th className="py-2 px-4 text-left font-medium">Subsection</th>
                <th className="py-2 px-4 text-left font-medium">Requirement</th>
                <th className="py-2 px-4 text-left font-medium">Type</th>
                <th className="py-2 px-4 text-left font-medium">Status</th>
                <th className="py-2 px-4 text-left font-medium">Documents</th>
                <th className="py-2 px-4 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requirements.map((req) => {
                // Get the status for this requirement
                const status = getRequirementStatus ? getRequirementStatus(req) : (req.status || 'Incomplete');
                
                return (
                  <RequirementRow
                    key={req.id}
                    requirement={req}
                    status={status}
                    isStatusLoading={isStatusLoading}
                    documentCounts={documentCounts}
                    isDocumentLoading={isLoading}
                    onViewGuidance={onViewGuidance}
                    onUploadDocumentation={handleUploadDocumentation}
                    onMarkNotApplicable={onMarkNotApplicable}
                    onMarkReadyForReview={onMarkReadyForReview}
                    onMarkIncomplete={onMarkIncomplete}
                    onStatusChange={handleStatusChange}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="py-8 text-center text-muted-foreground border rounded-md bg-muted/40">
          No requirements found for this standard.
        </div>
      )}

      {selectedRequirement && projectId && (
        <DocumentsDialog
          requirement={selectedRequirement}
          projectId={projectId}
          isOpen={isDocumentsDialogOpen}
          onClose={() => {
            setIsDocumentsDialogOpen(false);
            fetchDocumentCounts(); // Refresh document counts when dialog closes
          }}
        />
      )}
    </div>
  );
}
